'use client'

import { useStore } from '@tanstack/react-store'
import { AnimatePresence, motion } from 'motion/react'
import { X, Loader2 } from 'lucide-react'
import { cmsStore, discardPendingEdit, discardPendingImage, clearPendingEdits } from '#/lib/cms-store'
import type { PendingEdit } from '#/lib/cms-store'

const FILE_LABELS: Record<string, string> = {
  'menu-items': 'Menu Items',
  'menu-categories': 'Menu Categories',
  'hero-slides': 'Hero Slides',
  'core-values': 'Core Values',
  'team-members': 'Team Members',
  'blog-posts': 'Blog Posts',
  'bakery-stats': 'Bakery Stats',
  'site-config': 'Site Config',
  'hours': 'Hours',
  'services-info': 'Services',
  'reviews': 'Reviews',
}

const TYPE_STYLES: Record<string, string> = {
  update: 'bg-white/15 text-white/80',
  add: 'bg-emerald-500/25 text-emerald-200',
  delete: 'bg-red-500/25 text-red-200',
}

const TYPE_LABELS: Record<string, string> = {
  update: 'Edit',
  add: 'Add',
  delete: 'Delete',
}

function getEditLabel(edit: PendingEdit) {
  const name = (edit.data.name ?? edit.data.title ?? edit.data.headline) as string | undefined
  if (edit.type === 'add' && name) return name
  if (edit.type === 'delete') return edit.id ?? 'item'
  if (edit.type === 'update') {
    const fields = Object.keys(edit.data)
    if (fields.length <= 3) return fields.join(', ')
    return `${fields.slice(0, 2).join(', ')} +${fields.length - 2}`
  }
  return edit.id ?? 'item'
}

interface PublishPanelProps {
  open: boolean
  onClose: () => void
  onPublish: () => void
  isPublishing: boolean
}

export default function PublishPanel({ open, onClose, onPublish, isPublishing }: PublishPanelProps) {
  const pendingEdits = useStore(cmsStore, (s) => s.pendingEdits)
  const pendingImages = useStore(cmsStore, (s) => s.pendingImages)

  const editEntries = Object.entries(pendingEdits)
  const imageEntries = Object.entries(pendingImages)
  const totalCount = editEntries.length + imageEntries.length

  // Group edits by file
  const groups: Record<string, Array<[string, PendingEdit]>> = {}
  for (const entry of editEntries) {
    const file = entry[1].file
    if (!groups[file]) groups[file] = []
    groups[file].push(entry)
  }

  function handleDiscardAll() {
    if (window.confirm('Discard all pending changes?')) {
      clearPendingEdits()
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[9997]" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed bottom-14 left-4 z-[9998] w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-[#2a1214] shadow-2xl ring-1 ring-white/10"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h3 className="text-sm font-semibold text-white">
                Pending Changes
              </h3>
              <button
                onClick={onClose}
                className="rounded-md p-0.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Change list */}
            <div className="max-h-[50vh] overflow-y-auto">
              <div className="p-3 space-y-3">
                {Object.entries(groups).map(([file, edits]) => (
                  <div key={file}>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                      {FILE_LABELS[file] ?? file}
                    </p>
                    <div className="space-y-1">
                      {edits.map(([key, edit]) => (
                        <div
                          key={key}
                          className="group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-white/5"
                        >
                          <span
                            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${TYPE_STYLES[edit.type]}`}
                          >
                            {TYPE_LABELS[edit.type]}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-white/70">
                            {getEditLabel(edit)}
                          </span>
                          <button
                            onClick={() => discardPendingEdit(edit.file, edit.id)}
                            className="shrink-0 rounded p-0.5 text-white/30 opacity-0 transition-all hover:bg-white/10 hover:text-red-300 group-hover:opacity-100"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {imageEntries.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                      Images
                    </p>
                    <div className="space-y-1">
                      {imageEntries.map(([path]) => (
                        <div
                          key={path}
                          className="group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-white/5"
                        >
                          <span className="shrink-0 rounded bg-blue-500/25 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-200">
                            Upload
                          </span>
                          <span className="min-w-0 flex-1 truncate text-white/70">
                            {path.split('/').pop()}
                          </span>
                          <button
                            onClick={() => discardPendingImage(path)}
                            className="shrink-0 rounded p-0.5 text-white/30 opacity-0 transition-all hover:bg-white/10 hover:text-red-300 group-hover:opacity-100"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
              <button
                onClick={handleDiscardAll}
                className="text-[11px] font-medium text-white/40 transition-colors hover:text-red-300"
              >
                Discard All
              </button>
              <button
                onClick={() => {
                  onPublish()
                  onClose()
                }}
                disabled={isPublishing || totalCount === 0}
                className="flex items-center gap-1.5 rounded-full bg-white/20 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/30 disabled:opacity-50"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="size-3 animate-spin" />
                    Publishing
                  </>
                ) : (
                  `Publish ${totalCount} ${totalCount === 1 ? 'change' : 'changes'}`
                )}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
